import React, { useState, useEffect, useContext } from "react";
import styled from "@emotion/styled";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import { Link, useNavigate } from "react-router-dom";

import {
  getManageSubscriptionURL,
  orderSuccess,
  postCheckout,
  getCSShieldStats,
} from "../utils/api";
import { MainContext } from "./Main";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Tooltip from "@mui/material/Tooltip";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

const PRICING_TIERS = [
  { limit: 1000, price: 10 },
  { limit: 5000, price: 39 },
  { limit: 10000, price: 79 },
  { limit: 25000, price: 149 },
  { limit: 50000, price: 249 },
  { limit: 100000, price: 399 },
  { limit: 150000, price: 599 },
  { limit: 200000, price: 749 },
];

// Product IDs
const MONTHLY_STRIPE_SUB_ID =
  process.env.NODE_ENV === "development"
    ? "price_1U3cUqIwJheCLZQ8bdFYI58S"
    : "price_1QewQXIwJheCLZQ87fVwUdvM";

type productDisplayProps = {
  onShowMessage: (message: string) => void;
  imagesChecked: number;
};
const ProductDisplay = ({
  onShowMessage,
  imagesChecked,
}: productDisplayProps) => {
  const context = useContext(MainContext);
  const navigate = useNavigate();

  const checkoutClick = (volumeLimit: number) => {
    onCheckout("subscribe", volumeLimit);
  };

  const onCheckout = async (checkOutType: string, volumeLimit: number) => {
    let res = null;
    if (checkOutType === "subscribe") {
      res = await postCheckout(
        MONTHLY_STRIPE_SUB_ID,
        "IMAGE_BLOCKER",
        volumeLimit,
        null,
      );
    }

    if (res?.ok) {
      const resData = await res.json();
      if (resData === "User already a subscriber") {
        onShowMessage(
          "You are already a subscriber. Please refresh your browser or send us a message for help.",
        );
      } else if (resData?.url) {
        window.location.replace(resData.url);
      }
    } else {
      let errorMessage =
        "Something went wrong. Please try again or send us a message for help.";
      try {
        const errorData = await res?.json();
        if (typeof errorData === "string") {
          errorMessage = errorData;
        }
      } catch {
        // Keep default error message.
      }
      onShowMessage(errorMessage);
    }
  };

  const onLoginClick = () => {
    navigate("/login");
  };

  const onManageSubscriptionClick = async () => {
    const res = await getManageSubscriptionURL();
    if (res?.url) {
      window.location.replace(res.url);
    }
  };

  type PurchaseButtonProps = {
    volumeLimit: number;
  };
  const PurchaseButton: React.FC<PurchaseButtonProps> = ({ volumeLimit }) => {
    if (!context?.user?.email) {
      return (
        <Button
          className="purchase-button"
          variant="contained"
          onClick={onLoginClick}
        >
          Log in to subscribe
        </Button>
      );
    } else if (context?.user?.explicitImageBlockerMembershipActive) {
      return null;
    } else {
      return (
        <Button
          className="purchase-button"
          id="subscribe"
          variant="contained"
          onClick={() => checkoutClick(volumeLimit)}
        >
          Select
        </Button>
      );
    }
  };

  const ContactUsButton = () => {
    return (
      <Button
        className="questions-btn"
        variant="contained"
        onClick={(e: React.MouseEvent<HTMLElement>) => {
          window.location.href = "mailto:support@thackd.com";
          e.preventDefault();
        }}
      >
        Contact Us
      </Button>
    );
  };

  return (
    <Styled>
      <div className="product">
        <div className="description">
          <Typography variant="h1" gutterBottom>
            Explicit Image Blocker Pricing
          </Typography>
        </div>

        <div className="pricing-cards-container">
          {context?.user?.isImageBlockerUser && (
            <Card className="pricing-card">
              <Typography variant="body1" gutterBottom className="top-gap">
                {context?.user?.explicitImageBlockerMembershipActive && (
                  <span className="emphasize-text">Subscription Active</span>
                )}
                {!context?.user?.explicitImageBlockerMembershipActive && (
                  <>
                    Days until your free trial expires:{" "}
                    <span className="emphasize-text">
                      {context.user?.explicitImageBlockerFreeTrialDaysRemaining >
                      0
                        ? context.user?.explicitImageBlockerFreeTrialDaysRemaining
                        : "Free trial expired. Please select a package below."}
                    </span>
                  </>
                )}
              </Typography>
              <Typography
                variant="body1"
                gutterBottom
                style={{ display: "flex", alignItems: "center" }}
              >
                Images scanned in last 4 weeks:{" "}
                <span
                  className="emphasize-text"
                  style={{ marginLeft: "0.25rem" }}
                >
                  {imagesChecked.toLocaleString()}
                </span>
                <Tooltip title="The number of images that have been scanned by our integration in the last 4 weeks">
                  <HelpOutlineIcon className="help-icon" fontSize="small" />
                </Tooltip>
              </Typography>

              {context?.user?.explicitImageBlockerMembershipActive && (
                <Button
                  className="login-button"
                  variant="contained"
                  onClick={onManageSubscriptionClick}
                >
                  Manage Subscription
                </Button>
              )}
            </Card>
          )}

          <Card className="pricing-card">
            <Typography variant="body1" gutterBottom>
              Pricing is based on the volume of images you scan each month. You
              pay monthly in advance for the volume you expect to need.
            </Typography>
            <Typography variant="body1" gutterBottom className="top-gap">
              All plans include:
            </Typography>
            <ul>
              <Typography variant="body1" gutterBottom>
                <li>30-day free trial</li>
                <li>Automatic scanning of incoming Intercom images</li>
                <li>
                  Blocking of explicit nudity, violence, hate symbols, and more
                </li>
                <li>Backup of blocked images for agent review</li>
                <li>Responsive support</li>
                <li>Cancel anytime</li>
              </Typography>
            </ul>

            <TableContainer component={Paper}>
              <Table className="table-container" aria-label="pricing table">
                <TableHead>
                  <TableRow>
                    <TableCell>Images scanned / month</TableCell>
                    <TableCell align="right">Price / month</TableCell>
                    <TableCell align="center">Purchase</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Up to 1,000</TableCell>
                    <TableCell align="right">$10</TableCell>
                    <TableCell align="center">
                      <PurchaseButton volumeLimit={1000} />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Up to 5,000</TableCell>
                    <TableCell align="right">$39</TableCell>
                    <TableCell align="center">
                      <PurchaseButton volumeLimit={5000} />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Up to 10,000</TableCell>
                    <TableCell align="right">$79</TableCell>
                    <TableCell align="center">
                      <PurchaseButton volumeLimit={10000} />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Up to 25,000</TableCell>
                    <TableCell align="right">$149</TableCell>
                    <TableCell align="center">
                      <PurchaseButton volumeLimit={25000} />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Up to 50,000</TableCell>
                    <TableCell align="right">$249</TableCell>
                    <TableCell align="center">
                      <PurchaseButton volumeLimit={50000} />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Up to 100,000</TableCell>
                    <TableCell align="right">$399</TableCell>
                    <TableCell align="center">
                      <PurchaseButton volumeLimit={100000} />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Up to 150,000</TableCell>
                    <TableCell align="right">$599</TableCell>
                    <TableCell align="center">
                      <PurchaseButton volumeLimit={150000} />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Up to 200,000</TableCell>
                    <TableCell align="right">$749</TableCell>
                    <TableCell align="center">
                      <PurchaseButton volumeLimit={200000} />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Additional volume</TableCell>
                    <TableCell align="right">
                      $149 per extra 50,000 images
                    </TableCell>
                    <TableCell align="center">
                      <ContactUsButton />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="body1" gutterBottom className="top-gap">
              You will receive reminders when you are approaching your limit to
              allow you to increase your limit. Any images over your limit will
              not be scanned.
            </Typography>
          </Card>
        </div>
      </div>
    </Styled>
  );
};

const SuccessDisplay = () => {
  return (
    <Styled>
      <div className="product Box-root">
        <div className="description Box-root">
          <h3>Subscription successful!</h3>
          <Typography variant="body1">
            Thank you! Your subscription was successful and we have sent a
            confirmation email to you.
          </Typography>
          <Typography variant="body1">
            You can manage your subscription any time by clicking the user
            avatar in the top right.
          </Typography>
          <div className="success-continue-btn">
            <Link to={"/"}>
              <Button variant="contained">Continue</Button>
            </Link>
          </div>
        </div>
      </div>
    </Styled>
  );
};

export default function PricingExplicitImageBlocker() {
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [success, setSuccess] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [imagesChecked, setImagesChecked] = useState(0);
  const context = useContext(MainContext);

  const onShowMessage = (message: string) => {
    setSnackbarMessage(message);
    setShowSnackbar(true);
  };

  const handleSnackbarClose = () => {
    setShowSnackbar(false);
  };

  useEffect(() => {
    const onSuccess = async (newSessionId: string) => {
      await orderSuccess(newSessionId, null);
      await context.refreshUser();
    };

    const query = new URLSearchParams(window.location.search);

    if (query.get("success")) {
      const newSessionId = query.get("session_id") || "";
      if (!success) {
        onSuccess(newSessionId);
        setSuccess(true);
      }
      setSessionId(newSessionId);
    }

    if (query.get("canceled")) {
      setSuccess(false);
      onShowMessage("Order canceled.");
    }
  }, [sessionId, success, context]);

  useEffect(() => {
    const getStats = async (userId: string) => {
      const stats = await getCSShieldStats(userId);
      setImagesChecked(stats["num_images_checked"]);
    };
    if (imagesChecked === 0 && context?.user) {
      getStats(context.user.id);
    }
  }, [context, imagesChecked]);

  if (success && sessionId !== "") {
    return <SuccessDisplay />;
  }

  return (
    <>
      <ProductDisplay
        onShowMessage={onShowMessage}
        imagesChecked={imagesChecked}
      />
      <Snackbar
        open={showSnackbar}
        autoHideDuration={8000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="error"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

const Styled = styled.div`
  display: flex;
  flex-direction: column;

  .bold {
    font-weight: bold;
  }

  .success-continue-btn {
    margin-top: 20px;
  }

  .pricing-cards-container {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    justify-content: flex-start;
    margin: 2rem 0 1rem 0;
    gap: 2rem;

    .pricing-card {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-start;
      width: 100%;
      max-width: 700px;
      min-height: 150px;
      padding: 1rem;

      .login-button {
        margin: 2rem 0 0.5rem 0;
        width: 220px;
      }

      .purchase-button {
        width: 100px;
      }

      .questions-btn {
        width: 150px;
      }
    }
  }

  .top-gap {
    margin-top: 3rem;
  }

  .price-estimate {
    margin-top: 1rem;
    margin-bottom: 3rem;
  }

  .table-container {
    max-width: 500px;
  }

  .emphasize-text {
    font-weight: bold;
  }

  .help-icon {
    margin-left: 0.3rem;
  }
`;
